/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { getAccessToken, isEmptyOrNull, useAuth } from '@SLR/shared-library';
import { useQuery } from '@tanstack/react-query';
import { KEYCLOAK_CONSENTS_URL, queryKeys } from 'configs';
import { useMemo } from 'react';

export type KeycloakConsent = {
  userId: string;
  clientId: string;
  applicationId: string;
  createdDate: number;
};

export const useGetSolutionConsents = () => {
  const token = getAccessToken();
  const { authProfile } = useAuth();
  const userId = useMemo(() => authProfile?.id, [authProfile]);

  return useQuery({
    queryKey: [queryKeys.getSolutionConsents, userId, token],
    queryFn: async () => {
      const response = await fetch(
        `${KEYCLOAK_CONSENTS_URL}/${userId}/consent`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      const keycloakConsent = (await response.json()) as KeycloakConsent[];
      return keycloakConsent;
    },
    enabled: !isEmptyOrNull(token) && !isEmptyOrNull(userId)
  });
};
